import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { User, ShoppingCart, Package, Users, BarChart3, UserCheck, Activity } from 'lucide-react';
import { useT } from '../context/LanguageContext';

function Dashboard() {
  const navigate = useNavigate();
  const { t } = useT();

  // Auth removed – showing guest view

  const features = [
    {
      icon: UserCheck,
      title: t('dashboard.feature.staffManagement'),
      available: true,
      path: '/staff-management'
    },
    {
      icon: Activity,
      title: t('dashboard.feature.userTracking'),
      available: true,
      path: '/user-tracking'
    },
    {
      icon: ShoppingCart,
      title: t('dashboard.feature.orders'),
      available: true,
    },
    {
      icon: Package,
      title: t('dashboard.feature.inventory'),
      available: true,
    },
    {
      icon: Users,
      title: t('dashboard.feature.staff'),
      available: true,
    },
    {
      icon: BarChart3,
      title: t('dashboard.feature.analytics'),
      available: true,
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white border-b shadow-sm">
        <div className="w-full px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">R</span>
              </div>
              <div>
                <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">RestoBoxx POS</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{t('common.guest')}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full p-6">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {t('dashboard.welcomeHeading')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <Card 
              key={index} 
              className={`hover:shadow-lg transition-shadow ${!feature.available && 'opacity-50'}`}
            >
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <CardTitle>{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full" 
                  disabled={!feature.available}
                  variant={feature.available ? "default" : "secondary"}
                  onClick={() => feature.path && navigate(feature.path)}
                >
                  {feature.available ? t('dashboard.open') : t('dashboard.accessRestricted')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="w-5 h-5 mr-2" />
              {t('dashboard.profile.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.profile.email')}</p>
                <p className="font-medium">{t('dashboard.profile.notProvided')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.profile.username')}</p>
                <p className="font-medium">{t('common.guest')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.profile.phone')}</p>
                <p className="font-medium">{t('dashboard.profile.notProvided')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('common.status')}</p>
                <p className="font-medium text-green-600">{t('common.active')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default Dashboard;
